





































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import GiganautVue from '@/components/Shared/GiganautVue';
import { AgencySettingsModule } from '@/store/modules/agencySettings';
import { ReportFilterParams } from '@/store/modules/managementReports';
import { getFiscalYearOptions, FiscalYearOption } from '@/lib/fiscalYears';

type PeriodMode = 'range' | 'period';
export type FilterMode = 'standard' | 'inactivity';

// Payload shapes emitted to the parent. Discriminated by `mode` so the parent
// can route to the right loader (managementReports vs inactivityReport).
export interface StandardApplyPayload {
  mode: 'standard';
  params: ReportFilterParams;
}
export interface InactivityApplyPayload {
  mode: 'inactivity';
  params: {
    lookbackDays: number;
    branchId: string | null;
    deskId: string | null;
    ownerId: string | null;
    includeNever: boolean;
  };
}
export type ApplyPayload = StandardApplyPayload | InactivityApplyPayload;

@Component({ name: 'ManagementReportFilters' })
export default class ManagementReportFilters extends GiganautVue {
  @Prop({ default: 'standard' }) mode!: FilterMode;

  showFromPicker = false;
  showToPicker = false;

  periodMode: PeriodMode = 'period';
  periodFrom = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().substring(0, 10);
  periodTo = new Date().toISOString().substring(0, 10);
  groupBy = 'week';
  branchId: string | null = null;
  deskId: string | null = null;
  userId: string | null = null;
  rpFiscalYear: number | null = null;
  selectedReportingPeriodId: string | null = null;

  // Inactivity-mode state
  lookbackDays = 30;
  includeNever = false;

  groupByOptions = [
    { value: 'day', text: 'Daily' },
    { value: 'week', text: 'Weekly' },
    { value: 'month', text: 'Monthly' },
  ];

  get branches() {
    return AgencySettingsModule.branches;
  }

  get filteredDesks() {
    if (!this.branchId) {
      const allDesks: any[] = [];
      AgencySettingsModule.branches.forEach(b => {
        if ((b as any).desks) {
          (b as any).desks.forEach((d: any) => allDesks.push(d));
        }
      });
      return allDesks;
    }
    const branch = AgencySettingsModule.branches.find((b: any) => b.id === this.branchId);
    return branch ? (branch as any).desks || [] : [];
  }

  get agencyUserItems() {
    return AgencySettingsModule.agencyUsers.map((u: any) => ({
      id: u.id,
      displayName: u.person ? u.person.firstName + ' ' + u.person.lastName : u.emailAddress,
    }));
  }

  get reportingPeriods() {
    return AgencySettingsModule.reportingPeriods;
  }

  get rpFiscalYearItems(): FiscalYearOption[] {
    return getFiscalYearOptions();
  }

  get filteredReportingPeriods() {
    const list = this.rpFiscalYear
      ? this.reportingPeriods.filter((p: any) => p.fiscalYear === this.rpFiscalYear)
      : this.reportingPeriods;
    return [...list]
      .sort((a: any, b: any) => {
        if (a.fiscalYear !== b.fiscalYear) return b.fiscalYear - a.fiscalYear;
        return a.periodNumber - b.periodNumber;
      })
      .map((p: any) => ({
        id: p.id,
        displayText: p.label ? p.label : 'P' + p.periodNumber + ' FY' + p.fiscalYear,
      }));
  }

  get canApply(): boolean {
    if (this.mode === 'inactivity') {
      return this.lookbackDays > 0;
    }
    if (this.periodMode === 'range') {
      return !!this.periodFrom && !!this.periodTo;
    }
    return !!this.selectedReportingPeriodId;
  }

  // When the parent toggles between report modes, re-apply automatically so
  // the new report renders with the user's already-chosen branch/desk/etc.
  @Watch('mode')
  onModeChanged() {
    if (this.canApply) this.applyFilters();
  }

  async mounted() {
    if (AgencySettingsModule.branches.length === 0) {
      AgencySettingsModule.getBranches();
    }
    if (AgencySettingsModule.agencyUsers.length === 0) {
      AgencySettingsModule.getAgencyUsers();
    }
    if (AgencySettingsModule.reportingPeriods.length === 0) {
      await AgencySettingsModule.getReportingPeriods();
    }
    this.selectCurrentReportingPeriod();
    if (this.canApply) {
      this.applyFilters();
    }
  }

  // Pick the reporting period that contains today as the default. Falls back
  // to the most recent period if no period covers today (e.g. seed gap between
  // fiscal years). Leaves selection alone if nothing reasonable can be chosen.
  selectCurrentReportingPeriod() {
    if (this.reportingPeriods.length === 0) return;
    const todayIso = new Date().toISOString().substring(0, 10);
    const current = this.reportingPeriods.find((p: any) =>
      p.startDate && p.endDate &&
      p.startDate.substring(0, 10) <= todayIso &&
      todayIso <= p.endDate.substring(0, 10),
    );
    const fallback = current || [...this.reportingPeriods]
      .filter((p: any) => p.startDate && p.endDate)
      .sort((a: any, b: any) => b.endDate.localeCompare(a.endDate))[0];
    if (!fallback) return;
    this.rpFiscalYear = (fallback as any).fiscalYear || null;
    this.selectedReportingPeriodId = (fallback as any).id;
    this.onReportingPeriodSelected();
  }

  onBranchChanged() {
    this.deskId = null;
  }

  // When the user flips between date range and reporting period mode, clear
  // the inactive mode's state so there's no stale selection hanging around.
  onPeriodModeChanged() {
    if (this.periodMode === 'range') {
      this.rpFiscalYear = null;
      this.selectedReportingPeriodId = null;
    } else {
      // Leave periodFrom/periodTo as-is; they'll get populated when the
      // user picks a reporting period.
    }
  }

  onRpFiscalYearChanged() {
    // If the current reporting period no longer belongs to the selected
    // fiscal year, clear it.
    if (this.rpFiscalYear && this.selectedReportingPeriodId) {
      const rp = this.reportingPeriods.find((p: any) => p.id === this.selectedReportingPeriodId);
      if (rp && rp.fiscalYear !== this.rpFiscalYear) {
        this.selectedReportingPeriodId = null;
      }
    }
  }

  onReportingPeriodSelected() {
    if (this.selectedReportingPeriodId) {
      const rp = this.reportingPeriods.find((p: any) => p.id === this.selectedReportingPeriodId);
      if (rp) {
        this.periodFrom = rp.startDate ? rp.startDate.substring(0, 10) : '';
        this.periodTo = rp.endDate ? rp.endDate.substring(0, 10) : '';
      }
    }
  }

  applyFilters() {
    if (this.mode === 'inactivity') {
      const payload: InactivityApplyPayload = {
        mode: 'inactivity',
        params: {
          lookbackDays: this.lookbackDays,
          branchId: this.branchId,
          deskId: this.deskId,
          ownerId: this.userId,
          includeNever: this.includeNever,
        },
      };
      this.$emit('apply', payload);
      return;
    }
    const payload: StandardApplyPayload = {
      mode: 'standard',
      params: {
        periodFrom: this.periodFrom,
        periodTo: this.periodTo,
        groupBy: this.groupBy,
        branchId: this.branchId || undefined,
        deskId: this.deskId || undefined,
        userId: this.userId || undefined,
      },
    };
    this.$emit('apply', payload);
  }
}
